import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { OFFERS, IMAGES, PRODUCTS } from "../../data/content";
import { COLOMBIA, DEPARTAMENTOS } from "../../data/colombia";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABELS = {
  x1: "1 Crema — $59.000 COP",
  x2: "2 Cremas — $80.000 COP",
  x3: "3 Cremas — $96.000 COP",
};

export const OrderForm = ({ selectedPlan }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    plan: "x3",
    department: "",
    city: "",
    address: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedPlan) setForm((f) => ({ ...f, plan: selectedPlan }));
  }, [selectedPlan]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setDepartment = (e) => {
    const department = e.target.value;
    setForm((f) => ({ ...f, department, city: "" }));
  };

  const cities = form.department ? COLOMBIA[form.department] || [] : [];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Completa nombre, correo y teléfono.");
      return;
    }
    if (!form.department || !form.city) {
      toast.error("Selecciona departamento y ciudad.");
      return;
    }
    if (!form.address) {
      toast.error("Ingresa la dirección de envío.");
      return;
    }
    setLoading(true);
    try {
      const planLabel = PLAN_LABELS[form.plan] || form.plan;
      await axios.post("https://backend.essentiabellezza.com/api/orders", { ...form, plan: planLabel });
      navigate("/gracias", { state: { name: form.name, plan: planLabel } });
    } catch (err) {
      toast.error("Ocurrió un error. Intenta de nuevo.");
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-line py-3 text-ink placeholder-ink/40 focus:border-accent focus:outline-none transition-colors";

  return (
    <section
      id="pedido"
      data-testid="order-section"
      className="bg-brand text-white"
    >
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2">
        {/* Left: image / copy */}
        <div className="relative hidden lg:block">
          <img
            src={PRODUCTS.pack3}
            alt="Hydra Power concentrado antirresecamiento para pies"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-brand/40" />
          <div className="relative h-full flex flex-col justify-end p-14">
            <div className="eyebrow text-accent mb-4">Haz tu pedido</div>
            <h2 className="font-serif font-light text-5xl leading-[0.95] tracking-tight">
              El ritual comienza
              <br />
              <span className="italic">con un paso</span>
            </h2>
            <p className="mt-6 text-white/70 max-w-sm">
              Déjanos tus datos y coordinamos el envío de Hydra Power hasta tu
              puerta. Sin complicaciones.
            </p>
          </div>
        </div>

        {/* Right: form */}
        <div className="px-6 md:px-14 py-20 md:py-28">
          <div className="lg:hidden eyebrow text-accent mb-4">Haz tu pedido</div>
          <h2 className="lg:hidden font-serif font-light text-4xl leading-[0.95] tracking-tight mb-10">
            El ritual comienza con un paso
          </h2>

          <form onSubmit={submit} data-testid="order-form" className="space-y-8">
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className="eyebrow text-white/60">Nombre</label>
                <input
                  data-testid="order-name"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Tu nombre completo"
                  className={inputCls + " text-white placeholder-white/40 border-white/25"}
                />
              </div>
              <div>
                <label className="eyebrow text-white/60">Teléfono</label>
                <input
                  data-testid="order-phone"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+57 300 000 0000"
                  className={inputCls + " text-white placeholder-white/40 border-white/25"}
                />
              </div>
            </div>

            <div>
              <label className="eyebrow text-white/60">Correo electrónico</label>
              <input
                data-testid="order-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="tucorreo@email.com"
                className={inputCls + " text-white placeholder-white/40 border-white/25"}
              />
            </div>

            <div>
              <label className="eyebrow text-white/60">Oferta seleccionada</label>
              <select
                data-testid="order-plan"
                value={form.plan}
                onChange={set("plan")}
                className={inputCls + " text-white border-white/25 [&>option]:text-ink"}
              >
                {OFFERS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {PLAN_LABELS[o.id]}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className="eyebrow text-white/60">Departamento</label>
                <select
                  data-testid="order-department"
                  value={form.department}
                  onChange={setDepartment}
                  className={inputCls + " text-white border-white/25 [&>option]:text-ink"}
                >
                  <option value="">Selecciona...</option>
                  {DEPARTAMENTOS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="eyebrow text-white/60">Ciudad</label>
                <select
                  data-testid="order-city"
                  value={form.city}
                  onChange={set("city")}
                  disabled={!form.department}
                  className={
                    inputCls +
                    " text-white border-white/25 [&>option]:text-ink disabled:opacity-40 disabled:cursor-not-allowed"
                  }
                >
                  <option value="">
                    {form.department ? "Selecciona..." : "Elige departamento primero"}
                  </option>
                  {cities.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="eyebrow text-white/60">Dirección de envío</label>
              <input
                data-testid="order-address"
                value={form.address}
                onChange={set("address")}
                placeholder="Calle / Carrera, número, barrio, apto..."
                className={inputCls + " text-white placeholder-white/40 border-white/25"}
              />
            </div>

            <div>
              <label className="eyebrow text-white/60">Mensaje (opcional)</label>
              <textarea
                data-testid="order-message"
                value={form.message}
                onChange={set("message")}
                rows={2}
                placeholder="Preferencias de entrega, preguntas..."
                className={inputCls + " text-white placeholder-white/40 border-white/25 resize-none"}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="order-submit"
              className="group inline-flex items-center gap-3 rounded-full bg-accent text-white pl-8 pr-3 py-3.5 text-sm hover:bg-white hover:text-brand transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Enviando..." : "Confirmar pedido"}
              <span className="grid place-items-center h-9 w-9 rounded-full bg-white/20 group-hover:bg-brand/10">
                {loading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
                )}
              </span>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
